import { useQuery } from '@apollo/client';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import Avatar, { TeamAvatar } from '../../core/components/Avatar.tsx';
import MultiSelectField from '../../core/components/Fields/MultiSelectField.tsx';
import { GET_PROJECT_SHARE_OPTIONS } from '../queries.ts';
import { displayName } from '../utils/displayName.ts';

// picks are "team:<id>" and "user:<id>"; the project rule allows one team, so a second team replaces the first
const TEAM_PREFIX = 'team:';
const USER_PREFIX = 'user:';

export const withOneTeam = (values: string[]) => {
    const teams = values.filter((value) => value.startsWith(TEAM_PREFIX));
    const keptTeam = teams[teams.length - 1];
    return values.filter((value) => !value.startsWith(TEAM_PREFIX) || value === keptTeam);
};

/** The picker's values for a stored pick (a team and/or members). */
export const toRecipientValues = (team?: { id: string } | null, members?: { id: string }[] | null) => [
    ...(team ? [`${TEAM_PREFIX}${team.id}`] : []),
    ...(members || []).map((member) => `${USER_PREFIX}${member.id}`),
];

/** The mutation arguments for the picker's values. */
export const splitRecipients = (values: string[]) => ({
    teamId: values.find((value) => value.startsWith(TEAM_PREFIX))?.slice(TEAM_PREFIX.length) ?? null,
    memberIds: values.filter((value) => value.startsWith(USER_PREFIX)).map((value) => value.slice(USER_PREFIX.length)),
});

/** "Team Sales, Anna Schmidt" — who a share or a scheduled run goes to. */
export const recipientsLabel = (team: any, members: any[] | null | undefined, t) =>
    [team && t('share.teamLabel', { team: team.name }), ...(members || []).map(displayName)].filter(Boolean).join(', ');

interface RecipientsFieldProps {
    value: string[];
    onValueChange: (value: string[]) => void;
    autoFocus?: boolean;
    placeholder?: string;
}

/** One of your teams and/or people from them (the project rule), with a typeahead: used to share a chat and to send
 *  scheduled runs to colleagues. */
export const RecipientsField = ({ value, onValueChange, autoFocus = false, placeholder }: RecipientsFieldProps) => {
    const { t } = useTranslation('chat');
    const { data } = useQuery(GET_PROJECT_SHARE_OPTIONS);
    const options = data?.projectShareOptions || { teams: [], users: [] };
    const recipientOptions = [
        ...options.teams.map((team) => ({
            value: `${TEAM_PREFIX}${team.id}`,
            label: team.name,
            description: t('share.teamOption'),
            leading: <TeamAvatar />,
        })),
        ...options.users.map((user) => ({
            value: `${USER_PREFIX}${user.id}`,
            label: displayName(user),
            description: user.email,
            leading: <Avatar person={user} />,
        })),
    ];
    return (
        <MultiSelectField
            autoFocus={autoFocus}
            options={recipientOptions}
            value={value}
            onValueChange={(next) => onValueChange(withOneTeam(next))}
            placeholder={placeholder ?? t('share.recipientsPlaceholder')}
            emptyMessage={t('share.noResults')}
            removeLabel={t('share.remove')}
        />
    );
};
